import { Routes } from "@angular/router";
import { loadTranslationResolver } from "@app/kernel/translations/resolvers/load-translations.resolver";
import { COMMON_GUARD_OPTIONS } from "@app/shared/auth/constants/permissions.constant";
import { AuthGuard } from "@app/shared/auth/guards/auth.guard";
import { TRANSLATION_PATHS } from "@app/shared/general/translation/constants/translation.constant";
import { MainLayoutComponent } from "@app/shared/general/layouts/main-layout/main-layout.component";
import { QueueLayoutComponent } from "@shared-core/general/queue/layout/queue-layout/queue-layout.component";
import { QueueLayoutAbstraction } from "@shared-core/general/queue/abstractions/queue-layout.abstraction";
import { QueueLayoutService } from "./shared/services/queue-layout.service";

/** Top-level routes for the queue application module. */
export const queueRoutes: Routes = [
  {
    // Pathless parent: scopes the queue layout bindings (footer location +
    // header fullscreen toggle) to this feature, so they stay inside the queue
    // application instead of leaking into the global app config.
    path: "",
    providers: [{ provide: QueueLayoutAbstraction, useClass: QueueLayoutService }],
    children: [
      {
        // Ahead of the empty-path sibling below on purpose: that one matches every
        // URL under `/queue` before its lazy children are consulted, so a named
        // segment has to be offered first to be reached at all.
        path: "statistics",
        component: MainLayoutComponent,
        // Guarded at the route, not only by the `*sxwCan` on the layout's template.
        // That directive renders nothing without the permission, which — now that
        // `/` lands here — meant a user who lacks it opened the application onto a
        // blank page and stayed there. The guard sends them to `/home` instead.
        canActivate: [AuthGuard],
        data: { authGuardOptions: COMMON_GUARD_OPTIONS.QUEUE.STATISTICS },
        // No translation resolver here: `statisticsRoutes` declares its own, so the
        // domain stays loadable on its own terms.
        loadChildren: () => import("./domains/statistics/statistics.routes").then(r => r.statisticsRoutes),
      },
      {
        path: "",
        component: MainLayoutComponent,
        loadChildren: () => import("./domains/ticket-management/ticket-management.routes").then(r => r.ticketManagementRoutes),
        canActivate: [AuthGuard],
        data: { authGuardOptions: COMMON_GUARD_OPTIONS.QUEUE.MANAGE },
        resolve: { translation: loadTranslationResolver([TRANSLATION_PATHS.QUEUE]) },
      },
      {
        path: "office-screen",
        component: QueueLayoutComponent,
        loadChildren: () => import("./domains/office-screen/office-screen.routes").then(r => r.officeScreenRoutes),
        canActivate: [AuthGuard],
        data: { authGuardOptions: COMMON_GUARD_OPTIONS.QUEUE.OFFICE_SCREEN },
        resolve: { translation: loadTranslationResolver([TRANSLATION_PATHS.QUEUE]) },
      },
      {
        // No layout component: the monitor is a public-facing board projected on
        // a dedicated screen, so it renders bare — without the shared queue
        // header and footer — and fills the whole viewport.
        path: "monitor",
        loadChildren: () => import("./domains/monitor/monitor.routes").then(r => r.monitorRoutes),
        canActivate: [AuthGuard],
        data: { authGuardOptions: COMMON_GUARD_OPTIONS.QUEUE.MONITOR },
        resolve: { translation: loadTranslationResolver([TRANSLATION_PATHS.QUEUE]) },
      },
    ],
  },
];
